import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const STORAGE_KEY = "folio.cart";

function loadInitial() {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn("[cart] cannot load persisted cart:", err);
    return [];
  }
}

/**
 * Fire a GA4 e-commerce event. Wrapped so analytics failures never affect UX.
 * event: "add_to_cart" | "remove_from_cart"
 * item: cart-line item (id, title, author, price)
 */
function trackCartEvent(event, item) {
  try {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const price = Number(item.price) || 0;
    window.gtag("event", event, {
      currency: "USD",
      value: price,
      items: [{
        item_id: item.id,
        item_name: item.title || "",
        item_brand: item.author || "",
        price,
        quantity: 1,
      }],
    });
  } catch (err) {
    // Analytics failures must never affect cart UX.
    console.warn("[cart] GA event skipped:", err);
  }
}


export function CartProvider({ children }) {
  // Lazy init from localStorage so the value is set synchronously, before any
  // effect (including the persist effect) has a chance to overwrite it.
  const [items, setItems] = useState(loadInitial);
  const isFirstRun = useRef(true);

  // Persist on every change AFTER the initial mount.
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      // localStorage can fail in private mode / quota exceeded — safe to skip persist.
      console.warn("Cart persist skipped:", err);
    }
  }, [items]);

  const has = useCallback((bookId) => items.some((it) => it.id === bookId), [items]);

  const add = useCallback((book) => {
    if (!book?.id) return;
    setItems((cur) => {
      if (cur.some((it) => it.id === book.id)) {
        toast(`"${book.title}" is already in your cart`);
        return cur;
      }
      toast.success(`Added "${book.title}" to cart`);
      const line = {
        id: book.id,
        title: book.title,
        author: book.author,
        price: Number(book.price) || 0,
        cover_url: book.cover_url || null,
        cover_path: book.cover_path || null,
      };
      trackCartEvent("add_to_cart", line);
      return [...cur, line];
    });
  }, []);

  const remove = useCallback((bookId) => {
    setItems((cur) => {
      const removed = cur.find((it) => it.id === bookId);
      if (removed) trackCartEvent("remove_from_cart", removed);
      return cur.filter((it) => it.id !== bookId);
    });
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.length;
  const subtotal = useMemo(() => items.reduce((s, it) => s + (Number(it.price) || 0), 0), [items]);

  const value = useMemo(
    () => ({ items, count, subtotal, has, add, remove, clear }),
    [items, count, subtotal, has, add, remove, clear]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
